/**
 * What the Missing sections card is saying, in one place.
 *
 * The card holds two different findings under one title:
 *
 * - **section gaps** — headings the rival covers and this page never mentions;
 * - **rank-driven gaps** — queries the rival outranks you on with ground you
 *   already cover.
 *
 * The review surface has rendered both since they were added. The Dashboard
 * peek counted only the first, so a page with three rank gaps and no section
 * gaps showed the row on the belt and nothing at all on the Dashboard — the
 * operator's first read of that page silently dropped its only content
 * finding. The count badge had the same hole once, which is why it already
 * carries the three-way logic reproduced here.
 *
 * Both surfaces now phrase this from the same functions, so they cannot count
 * or word it differently.
 */

/** "3 QUERIES", "1 SECTION", "4 GAPS", or null when there is nothing. */
export function missingSectionsCountLabel(sections: number, queries: number): string | null {
  const s = Math.max(0, sections | 0);
  const q = Math.max(0, queries | 0);
  const badge = (n: number, one: string, many: string) => (n === 1 ? `1 ${one}` : `${n} ${many}`);

  if (s && !q) return badge(s, 'SECTION', 'SECTIONS');
  if (q && !s) return badge(q, 'QUERY', 'QUERIES');
  if (!s && !q) return null;
  // Mixed: neither noun is true of the whole, so the only honest word is the
  // general one. This is the case the badge exists to generalise.
  return badge(s + q, 'GAP', 'GAPS');
}

/**
 * The peek's one-line fact, matching the belt's wording.
 *
 * The belt has room for two verdict lines and prints one per kind. The peek has
 * a single line, so a page carrying both is joined rather than given the louder
 * half — reporting one finding and hiding the other is how this row lost the
 * rank gaps in the first place.
 */
export function missingSectionsFact(
  domain: string | null | undefined,
  sections: number,
  queries: number
): string {
  const who = (domain || '').trim() || 'The top result';
  const s = Math.max(0, sections | 0);
  const q = Math.max(0, queries | 0);

  const sectionPart =
    s === 1 ? 'covers a section your page never mentions' : 'covers sections your page never mentions';
  const queryPart =
    q === 1
      ? 'outranks you on 1 query where you already have the page'
      : `outranks you on ${q} queries where you already have the page`;

  if (s && q) return `${who} ${sectionPart}, and ${queryPart}.`;
  if (q) return `${who} ${queryPart}.`;
  return `${who} ${sectionPart}.`;
}
